import { useEffect, useState } from 'react';
import { getFormations } from '../services/formationService';
import { creerDevisManuel, televerserDevisExterne } from '../services/devisService';
import { toast } from '../components/Toasts';
import { FORMATS_FORMATION as FORMATS } from '../utils/formatsFormation';
import { formatMontant } from './devisUtils';
import SelectRecherche from '../components/SelectRecherche';
import SelectVille from '../components/SelectVille';
import RemplirParTexte from '../components/RemplirParTexte';
import { IconeCorbeille } from './BoutonIcone';
import SelectSessionCalendrier, {
  estInter,
  useSessionsCalendrier,
} from '../components/SelectSessionCalendrier';
import '../assets/styles/DevisPanel.css';
import BarreProgression, { useProgression } from '../components/BarreProgression';

const LIGNE_VIDE = {
  formationId: '',
  calendrierId: '',
  format: 'inter_presentiel',
  dureeJours: '',
  nbParticipants: 1,
  tauxRemise: 0,
  // Remise en euros HT retirée à chaque journée facturée (vide : aucune).
  remiseJourneeHt: '',
  motifRemise: '',
  groupeLibelle: '',
  // Libellé affiché sur le devis ; vide : l'intitulé de la formation.
  libelle: '',
  // Une option est proposée en plus de la ligne de base : « en option » sur le
  // devis, hors total tant qu'elle n'est pas retenue.
  estOption: false,
  optionRetenue: false,
  // Vide : la grille tarifaire. Un prix saisi vaut pour ce devis seul.
  prixUnitaireHt: '',
};

/**
 * Le contact arrive soit d'une ligne de liste (snake_case), soit du panneau
 * de détail (camelCase Sequelize) : on accepte les deux graphies.
 */
function champContact(contact, snake, camel) {
  return contact?.[snake] ?? contact?.[camel] ?? '';
}

/**
 * Création d'un devis sans IA : l'utilisateur choisit les formations dans le
 * catalogue. Les prix ne sont pas saisis — ils viennent de la grille tarifaire
 * côté serveur, exactement comme pour un devis généré.
 */
const MOIS = [
  'janvier', 'février', 'mars', 'avril', 'mai', 'juin',
  'juillet', 'août', 'septembre', 'octobre', 'novembre', 'décembre',
];

/** 'aaaa-mm' (valeur d'un <input type="month">) -> « Septembre 2026 ». */
function moisTexte(valeur) {
  const [a, m] = String(valeur || '').split('-').map(Number);
  if (!a || !m || !MOIS[m - 1]) return '';
  const nom = MOIS[m - 1];
  return `${nom.charAt(0).toUpperCase()}${nom.slice(1)} ${a}`;
}

/**
 * La période souhaitée reste un texte sur le devis (imprimé tel quel, repris
 * par le contrat) : on le compose depuis deux mois choisis, début et fin.
 * Un seul mois quand la fin est vide ou égale au début.
 */
function periodeTexte(debut, fin) {
  const d = moisTexte(debut);
  const f = moisTexte(fin);
  if (!d && !f) return '';
  if (!d || !f || d === f) return d || f;
  return `${d} – ${f}`;
}

/**
 * Un mois et une année, chacun dans sa liste — et non un <input type="month">,
 * que Firefox et Safari de bureau rendent en simple champ texte. La valeur
 * reste 'aaaa-mm' ; un seul des deux choisi ne fait pas encore une date.
 */
const ANNEE_COURANTE = new Date().getFullYear();
const ANNEES = Array.from({ length: 5 }, (_, i) => ANNEE_COURANTE - 1 + i);

/**
 * Champs proposés à la zone « coller un texte » : l'entête du devis (client,
 * CSE, modalités, remises). Les lignes de formation en sont exclues : elles se
 * choisissent dans le catalogue, et un texte collé ne dit pas laquelle viser.
 */
const CHAMPS_IA = [
  { cle: 'siret', libelle: 'SIRET' },
  { cle: 'activite', libelle: 'Activité' },
  { cle: 'adresse', libelle: 'Adresse' },
  { cle: 'codePostal', libelle: 'Code postal' },
  { cle: 'ville', libelle: 'Ville' },
  { cle: 'conventionCollective', libelle: 'Convention collective' },
  { cle: 'cseEffectifEntreprise', libelle: 'Effectif entreprise', type: 'nombre' },
  { cle: 'cseNbElus', libelle: 'Nombre d’élus', type: 'nombre' },
  { cle: 'cseTitulaires', libelle: 'Titulaires', type: 'nombre' },
  { cle: 'cseSuppleants', libelle: 'Suppléants', type: 'nombre' },
  { cle: 'cseNbPremierMandat', libelle: 'Dont 1er mandat', type: 'nombre' },
  { cle: 'cseNbRenouvellement', libelle: 'Dont renouvellement', type: 'nombre' },
  { cle: 'cseNbDesignes', libelle: 'Référents à désigner', type: 'nombre' },
  { cle: 'cseDernieresElections', libelle: 'Dernières élections', type: 'date' },
  { cle: 'cseContexte', libelle: 'Contexte' },
  { cle: 'periodeDebut', libelle: 'Période souhaitée — début (mois au format aaaa-mm)' },
  { cle: 'periodeFin', libelle: 'Période souhaitée — fin (mois au format aaaa-mm)' },
  { cle: 'modalitesLieu', libelle: 'Lieu' },
  {
    cle: 'modalitesFormatSouhaite',
    libelle: 'Format souhaité',
    type: 'choix',
    options: [...FORMATS, { value: 'mixte', label: 'Mixte (INTRA / INTER)' }].map((f) => ({
      valeur: f.value,
      libelle: f.label,
    })),
  },
  { cle: 'modalitesContraintes', libelle: 'Contraintes' },
  { cle: 'resumeEchange', libelle: 'Résumé de l’échange (affiché en tête du PDF)' },
  { cle: 'conditionRemise', libelle: 'Conditions des remises (note sous le tableau)' },
  { cle: 'offreLibelle', libelle: 'Offre exceptionnelle (libellé, sous les remises)' },
  { cle: 'offreHt', libelle: 'Montant de l’offre (€ HT)', type: 'nombre' },
  { cle: 'lignePrixUnitaireHt', libelle: 'Prix unitaire HT écrit dans le texte, pour une journée (€) — la première formation', type: 'nombre' },
  { cle: 'ligneTauxRemise', libelle: 'Remise en % écrite dans le texte — la première formation', type: 'nombre' },
  { cle: 'ligneRemiseJourneeHt', libelle: 'Remise par journée écrite dans le texte (€) — la première formation', type: 'nombre' },
];

function ChoixMoisAnnee({ libelle, valeur, onChange }) {
  const [annee, mois] = String(valeur || '').split('-');
  const changer = (a, m) => onChange(a && m ? `${a}-${m}` : '');
  return (
    <div className="devis-form-field">
      <span>{libelle}</span>
      <div className="devis-mois-annee">
        <SelectRecherche
          value={mois || ''}
          onChange={(e) => changer(annee || (e.target.value ? String(ANNEE_COURANTE) : ''), e.target.value)}
          aria-label={`${libelle} : mois`}
        >
          <option value="">— mois —</option>
          {MOIS.map((nom, i) => (
            <option key={nom} value={String(i + 1).padStart(2, '0')}>
              {nom.charAt(0).toUpperCase() + nom.slice(1)}
            </option>
          ))}
        </SelectRecherche>
        <SelectRecherche
          value={annee || ''}
          onChange={(e) => changer(e.target.value, mois || '')}
          aria-label={`${libelle} : année`}
        >
          <option value="">— année —</option>
          {ANNEES.map((a) => (
            <option key={a} value={String(a)}>
              {a}
            </option>
          ))}
        </SelectRecherche>
      </div>
    </div>
  );
}

function DevisManuelPanel({ contact, raison, onClose, onCree }) {
  const [formations, setFormations] = useState([]);
  // Sessions INTER à venir : le devis signé y prendra ses places.
  const sessionsCalendrier = useSessionsCalendrier();
  const [chargement, setChargement] = useState(true);
  const [lignes, setLignes] = useState([{ ...LIGNE_VIDE }]);
  const [modalites, setModalites] = useState({
    // Mois de début et de fin ('aaaa-mm') ; le texte du devis en est dérivé.
    periodeDebut: '',
    periodeFin: '',
    modalitesLieu: '',
    modalitesFormatSouhaite: '',
    modalitesContraintes: '',
  });

  // Coordonnées client : pré-remplies depuis la demande, complétables ici.
  const [client, setClient] = useState(() => ({
    siret: champContact(contact, 'siret', 'siret'),
    adresse: champContact(contact, 'adresse', 'adresse'),
    codePostal: champContact(contact, 'code_postal', 'codePostal'),
    ville: champContact(contact, 'ville', 'ville'),
    activite: champContact(contact, 'activite', 'activite'),
    conventionCollective: champContact(
      contact,
      'convention_collective',
      'conventionCollective'
    ),
  }));

  const [cse, setCse] = useState({
    cseEffectifEntreprise: champContact(contact, 'effectif_nombre', 'effectifNombre'),
    // Le nombre d'élus de la demande est un texte libre : on n'en garde que le nombre.
    cseNbElus: parseInt(champContact(contact, 'nb_elus', 'nbElus'), 10) || '',
    cseTitulaires: '',
    cseSuppleants: '',
    cseNbPremierMandat: '',
    cseNbRenouvellement: '',
    cseNbDesignes: '',
    cseDernieresElections: '',
    cseContexte: '',
  });
  // Remises et offre exceptionnelle du devis.
  const [remises, setRemises] = useState({ conditionRemise: '', offreLibelle: '', offreHt: '', resumeEchange: '' });
  const [saving, setSaving] = useState(false);
  const progressionEnregistrement = useProgression(saving, { estimeeMs: 8000 });
  const [erreur, setErreur] = useState(null);
  // PDF d'un devis établi hors de l'application (facultatif) : il devient le
  // document du devis, marqué « externe ».
  const [fichierExterne, setFichierExterne] = useState(null);

  useEffect(() => {
    getFormations({ actif: true })
      .then((res) => setFormations(res.data || []))
      .catch(() => setErreur('Impossible de charger le catalogue de formations.'))
      .finally(() => setChargement(false));
  }, []);

  function majLigne(index, champ, valeur) {
    setLignes((prev) =>
      prev.map((l, i) => {
        if (i !== index) return l;
        const suivant = { ...l, [champ]: valeur };
        // Sélectionner une formation pré-remplit sa durée de référence, et
        // défait la session : elle portait l'ancienne formation.
        if (champ === 'formationId') {
          const f = formations.find((x) => String(x.id) === String(valeur));
          if (f && !l.dureeJours) suivant.dureeJours = Number(f.duree_jours ?? f.dureeJours);
          suivant.calendrierId = '';
        }
        // Une ligne INTRA n'a pas de session : sa date se négocie au contrat.
        if (champ === 'format' && !estInter(valeur)) suivant.calendrierId = '';
        return suivant;
      })
    );
  }

  /**
   * Reporte les valeurs lues par l'IA dans les trois blocs d'état de l'entête,
   * comme le font leurs <input> (chaînes), sans toucher aux champs absents.
   * Un mois ou une date mal formés sont ignorés plutôt que laissés au hasard.
   */
  function appliquerValeursIA(v) {
    const valides = { ...v };
    const moisValide = (m) => /^\d{4}-(0[1-9]|1[0-2])$/.test(m) && ANNEES.includes(Number(m.slice(0, 4)));
    for (const cle of ['periodeDebut', 'periodeFin']) {
      if (cle in valides && !moisValide(String(valides[cle]))) delete valides[cle];
    }
    if ('cseDernieresElections' in valides && !/^\d{4}-\d{2}-\d{2}$/.test(String(valides.cseDernieresElections))) {
      delete valides.cseDernieresElections;
    }
    const pris = (cles) =>
      Object.fromEntries(
        cles.filter((c) => valides[c] !== undefined && valides[c] !== null).map((c) => [c, String(valides[c])])
      );
    setClient((prev) => ({
      ...prev,
      ...pris(['siret', 'activite', 'adresse', 'codePostal', 'ville', 'conventionCollective']),
    }));
    setCse((prev) => ({
      ...prev,
      ...pris([
        'cseEffectifEntreprise', 'cseNbElus', 'cseTitulaires', 'cseSuppleants',
        'cseNbPremierMandat', 'cseNbRenouvellement', 'cseNbDesignes',
        'cseDernieresElections', 'cseContexte',
      ]),
    }));
    const formatsPermis = [...FORMATS.map((f) => f.value), 'mixte'];
    if ('modalitesFormatSouhaite' in valides && !formatsPermis.includes(String(valides.modalitesFormatSouhaite))) {
      delete valides.modalitesFormatSouhaite;
    }
    setModalites((prev) => ({
      ...prev,
      ...pris(['periodeDebut', 'periodeFin', 'modalitesLieu', 'modalitesFormatSouhaite', 'modalitesContraintes']),
    }));
    setRemises((prev) => ({
      ...prev,
      ...pris(['conditionRemise', 'offreLibelle', 'offreHt', 'resumeEchange']),
    }));
    // Les prix écrits dans le texte valent pour la première ligne du devis, pas
    // ceux de la grille : le catalogue propose, le devis dispose.
    const prixLigne = pris(['lignePrixUnitaireHt', 'ligneTauxRemise', 'ligneRemiseJourneeHt']);
    if (Object.keys(prixLigne).length) {
      setLignes((prev) =>
        prev.map((l, i) =>
          i === 0
            ? {
                ...l,
                ...(prixLigne.lignePrixUnitaireHt !== undefined && { prixUnitaireHt: prixLigne.lignePrixUnitaireHt }),
                ...(prixLigne.ligneTauxRemise !== undefined && { tauxRemise: prixLigne.ligneTauxRemise }),
                ...(prixLigne.ligneRemiseJourneeHt !== undefined && { remiseJourneeHt: prixLigne.ligneRemiseJourneeHt }),
              }
            : l
        )
      );
    }
  }

  /**
   * Les lignes lues par l'IA remplacent celles du formulaire : formation, format,
   * durée, effectif, prix et remises écrits, options (plusieurs par formation :
   * une ligne chacune, juste après la ligne de base). Un prix absent reste vide :
   * c'est la grille du catalogue qui s'applique à l'enregistrement.
   */
  function appliquerLignesIA(lues) {
    setLignes(
      lues.map((lue) => {
        const f = formations.find((x) => String(x.id) === String(lue.formationId));
        return {
          ...LIGNE_VIDE,
          formationId: String(lue.formationId),
          format: lue.format || LIGNE_VIDE.format,
          dureeJours: lue.dureeJours ?? Number(f?.duree_jours ?? f?.dureeJours ?? 1),
          nbParticipants: lue.nbParticipants ?? 1,
          prixUnitaireHt: lue.prixUnitaireHt ?? '',
          tauxRemise: lue.tauxRemise ?? 0,
          remiseJourneeHt: lue.remiseJourneeHt ?? '',
          libelle: lue.libelle || '',
          groupeLibelle: lue.groupeLibelle || '',
          estOption: lue.estOption,
          optionRetenue: lue.estOption && lue.optionRetenue,
        };
      })
    );
  }

  const ajouterLigne = () => setLignes((prev) => [...prev, { ...LIGNE_VIDE }]);
  const retirerLigne = (i) => setLignes((prev) => prev.filter((_, k) => k !== i));
  const nbLignesDeBase = lignes.filter((l) => !l.estOption).length;

  /**
   * Une option de la formation de la ligne : même formation, même format, même
   * durée et même effectif, à ajuster. Elle se range après la ligne de base et
   * ses options déjà posées.
   */
  function ajouterOption(index) {
    setLignes((prev) => {
      const base = prev[index];
      let fin = index;
      while (fin + 1 < prev.length && prev[fin + 1].estOption) fin += 1;
      const option = {
        ...base,
        calendrierId: '',
        prixUnitaireHt: '',
        tauxRemise: 0,
        remiseJourneeHt: '',
        motifRemise: '',
        libelle: '',
        estOption: true,
        optionRetenue: false,
      };
      return [...prev.slice(0, fin + 1), option, ...prev.slice(fin + 1)];
    });
  }

  async function soumettre(e) {
    e.preventDefault();
    setSaving(true);
    setErreur(null);

    const vide = (v) => (String(v ?? '').trim() === '' ? null : v);
    const nombre = (v) => (v === '' || v === null ? null : Number(v));

    if (modalites.periodeDebut && modalites.periodeFin && modalites.periodeFin < modalites.periodeDebut) {
      setErreur('La fin de la période souhaitée précède son début.');
      setSaving(false);
      return;
    }

    try {
      const { periodeDebut, periodeFin, ...resteModalites } = modalites;
      const res = await creerDevisManuel(contact.id, {
        ...resteModalites,
        modalitesPeriodeTexte: vide(periodeTexte(periodeDebut, periodeFin)),
        client: {
          siret: vide(client.siret),
          adresse: vide(client.adresse),
          codePostal: vide(client.codePostal),
          ville: vide(client.ville),
          activite: vide(client.activite),
          conventionCollective: vide(client.conventionCollective),
        },
        cseEffectifEntreprise: nombre(cse.cseEffectifEntreprise),
        cseNbElus: nombre(cse.cseNbElus),
        cseTitulaires: nombre(cse.cseTitulaires),
        cseSuppleants: nombre(cse.cseSuppleants),
        cseNbPremierMandat: nombre(cse.cseNbPremierMandat),
        cseNbRenouvellement: nombre(cse.cseNbRenouvellement),
        cseNbDesignes: nombre(cse.cseNbDesignes),
        cseDernieresElections: vide(cse.cseDernieresElections),
        cseContexte: vide(cse.cseContexte),
        conditionRemise: vide(remises.conditionRemise),
        offreLibelle: vide(remises.offreLibelle),
        offreHt: remises.offreHt === '' ? 0 : Number(remises.offreHt),
        resumeEchange: vide(remises.resumeEchange),
        lignes: lignes.map((l) => ({
          formationId: Number(l.formationId),
          calendrierId: l.calendrierId ? Number(l.calendrierId) : null,
          format: l.format,
          dureeJours: Number(l.dureeJours),
          nbParticipants: Number(l.nbParticipants),
          prixUnitaireHt:
            l.prixUnitaireHt === '' || l.prixUnitaireHt == null ? null : Number(l.prixUnitaireHt),
          tauxRemise: Number(l.tauxRemise) || 0,
          remiseJourneeHt: Number(l.remiseJourneeHt) || 0,
          motifRemise: l.motifRemise || null,
          groupeLibelle: l.groupeLibelle || null,
          libelle: l.libelle ? l.libelle.trim() : null,
          estOption: Boolean(l.estOption),
          optionRetenue: Boolean(l.estOption && l.optionRetenue),
        })),
      });
      let cree = res.data;
      if (fichierExterne) {
        try {
          const televerse = await televerserDevisExterne(cree.id, fichierExterne);
          cree = televerse.data || cree;
        } catch (errFichier) {
          // Le devis existe déjà : on ne le recrée pas, on signale le fichier manquant.
          toast.erreur(
            `Devis créé, mais le PDF n’a pas pu être téléversé : ${errFichier.message}. Rouvrez la fiche pour réessayer.`
          );
        }
      }
      onCree?.(cree);
    } catch (err) {
      setErreur(err.message || 'Création impossible.');
    } finally {
      setSaving(false);
    }
  }

  /**
   * Aperçu indicatif : le prix saisi sur la ligne, sinon le tarif du
   * catalogue (INTER par participant, INTRA = INTER × 3 par groupe). Le
   * serveur reste seul maître des montants.
   */
  const apercuLignes = lignes.reduce((total, l) => {
    // Une option non retenue n'entre pas dans le total.
    if (l.estOption && !l.optionRetenue) return total;
    const f = formations.find((x) => String(x.id) === String(l.formationId));
    if (!f) return total;
    const inter = Number(f.prixJourInterHt ?? f.prix_jour_inter_ht) || 300;
    const grille = l.format.startsWith('intra') ? inter * 3 : inter;
    const pu = l.prixUnitaireHt !== '' && l.prixUnitaireHt != null ? Number(l.prixUnitaireHt) : grille;
    const brut =
      l.format.startsWith('intra')
        ? pu * Number(l.dureeJours || 0)
        : pu * Number(l.dureeJours || 0) * Number(l.nbParticipants || 1);
    const joursFactures = l.format.startsWith('intra')
      ? Number(l.dureeJours || 0)
      : Number(l.dureeJours || 0) * Number(l.nbParticipants || 1);
    const remise = Math.min(
      (brut * (Number(l.tauxRemise) || 0)) / 100 + (Number(l.remiseJourneeHt) || 0) * joursFactures,
      brut
    );
    return total + brut - remise;
  }, 0);
  const apercu = Math.max(0, apercuLignes - (Number(remises.offreHt) || 0));

  const valide =
    lignes.every((l) => l.formationId && Number(l.dureeJours) > 0) && lignes.some((l) => !l.estOption);

  return (
    <div className="devis-modal-overlay" onClick={onClose}>
      <div className="devis-modal" onClick={(e) => e.stopPropagation()}>
        <div className="devis-modal-header">
          <div>
            <div className="devis-modal-numero">Devis manuel</div>
            <div className="devis-modal-sub">
              {contact.societeNom || contact.societe_nom} · {contact.prenom} {contact.nom}
            </div>
          </div>
          <div className="devis-modal-header-right">
            <button type="button" className="devis-drawer-close" onClick={onClose} aria-label="Fermer">
              ×
            </button>
          </div>
        </div>

        <div className="devis-modal-body">
          {raison && (
            <div className="devis-alert devis-alert-attention">
              <strong>Génération automatique impossible.</strong>
              <div>{raison}</div>
              <div>Composez le devis à la main ci-dessous.</div>
            </div>
          )}
          {erreur && <div className="devis-alert devis-alert-erreur">{erreur}</div>}

          {chargement && <div className="devis-state">Chargement du catalogue...</div>}

          {!chargement && (
            <form onSubmit={soumettre}>
              <RemplirParTexte
                contexte="devis d'une formation CSE créé à la main : coordonnées du client, composition du CSE, modalités, remises"
                champs={CHAMPS_IA}
                onValeurs={appliquerValeursIA}
                formations={formations.map((f) => ({ id: f.id, code: f.code, intitule: f.intitule }))}
                onLignes={appliquerLignesIA}
              />

              <div className="devis-form-section-title">Informations client</div>
              <div className="devis-form-row">
                <label className="devis-form-field">
                  <span>Société</span>
                  <input
                    type="text"
                    value={champContact(contact, 'societe_nom', 'societeNom')}
                    disabled
                    title="La société provient de la demande d'origine"
                  />
                </label>
                <label className="devis-form-field">
                  <span>SIRET</span>
                  <input
                    type="text"
                    value={client.siret}
                    onChange={(e) => setClient({ ...client, siret: e.target.value })}
                  />
                </label>
                <label className="devis-form-field">
                  <span>Activité</span>
                  <input
                    type="text"
                    value={client.activite}
                    onChange={(e) => setClient({ ...client, activite: e.target.value })}
                  />
                </label>
              </div>
              <div className="devis-form-row">
                <label className="devis-form-field" style={{ flex: 2 }}>
                  <span>Adresse</span>
                  <input
                    type="text"
                    value={client.adresse}
                    onChange={(e) => setClient({ ...client, adresse: e.target.value })}
                  />
                </label>
                <label className="devis-form-field">
                  <span>Code postal</span>
                  <input
                    type="text"
                    value={client.codePostal}
                    onChange={(e) => setClient({ ...client, codePostal: e.target.value })}
                  />
                </label>
                <label className="devis-form-field">
                  <span>Ville</span>
                  <SelectVille
                    value={client.ville}
                    onChange={(e) => setClient((prev) => ({ ...prev, ville: e.target.value }))}
                    onChoisir={({ codePostal }) =>
                      setClient((prev) => ({ ...prev, codePostal: prev.codePostal || codePostal }))
                    }
                    placeholder="Tapez une ville de France"
                  />
                </label>
              </div>
              <label className="devis-form-field">
                <span>Convention collective</span>
                <input
                  type="text"
                  value={client.conventionCollective}
                  onChange={(e) =>
                    setClient({ ...client, conventionCollective: e.target.value })
                  }
                />
              </label>
              <div className="devis-note-champ">
                Enregistrées pour les prochains devis de ce client.
              </div>

              <div className="devis-form-section-title">Composition du CSE</div>
              <div className="devis-form-row">
                <label className="devis-form-field">
                  <span>Effectif entreprise</span>
                  <input
                    type="number"
                    min="0"
                    value={cse.cseEffectifEntreprise}
                    onChange={(e) =>
                      setCse({ ...cse, cseEffectifEntreprise: e.target.value })
                    }
                  />
                </label>
                <label className="devis-form-field">
                  <span>Nombre d’élus</span>
                  <input
                    type="number"
                    min="0"
                    value={cse.cseNbElus}
                    onChange={(e) => setCse({ ...cse, cseNbElus: e.target.value })}
                  />
                </label>
                <label className="devis-form-field">
                  <span>Titulaires</span>
                  <input
                    type="number"
                    min="0"
                    value={cse.cseTitulaires}
                    onChange={(e) => setCse({ ...cse, cseTitulaires: e.target.value })}
                  />
                </label>
                <label className="devis-form-field">
                  <span>Suppléants</span>
                  <input
                    type="number"
                    min="0"
                    value={cse.cseSuppleants}
                    onChange={(e) => setCse({ ...cse, cseSuppleants: e.target.value })}
                  />
                </label>
              </div>
              <div className="devis-form-row">
                <label className="devis-form-field">
                  <span>Dont 1er mandat</span>
                  <input
                    type="number"
                    min="0"
                    value={cse.cseNbPremierMandat}
                    onChange={(e) => setCse({ ...cse, cseNbPremierMandat: e.target.value })}
                  />
                </label>
                <label className="devis-form-field">
                  <span>Dont renouvellement</span>
                  <input
                    type="number"
                    min="0"
                    value={cse.cseNbRenouvellement}
                    onChange={(e) => setCse({ ...cse, cseNbRenouvellement: e.target.value })}
                  />
                </label>
                <label className="devis-form-field">
                  <span>Référents à désigner</span>
                  <input
                    type="number"
                    min="0"
                    value={cse.cseNbDesignes}
                    onChange={(e) => setCse({ ...cse, cseNbDesignes: e.target.value })}
                  />
                </label>
                <label className="devis-form-field">
                  <span>Dernières élections</span>
                  <input
                    type="date"
                    value={cse.cseDernieresElections}
                    onChange={(e) =>
                      setCse({ ...cse, cseDernieresElections: e.target.value })
                    }
                  />
                </label>
              </div>
              <label className="devis-form-field">
                <span>Contexte</span>
                <input
                  type="text"
                  placeholder="Renouvellement CSE en mai 2026"
                  value={cse.cseContexte}
                  onChange={(e) => setCse({ ...cse, cseContexte: e.target.value })}
                />
              </label>

              <div className="devis-form-section-title">Modalités</div>
              <div className="devis-form-row">
                <ChoixMoisAnnee
                  libelle="Période souhaitée — début"
                  valeur={modalites.periodeDebut}
                  onChange={(v) => setModalites({ ...modalites, periodeDebut: v })}
                />
                <ChoixMoisAnnee
                  libelle="Période souhaitée — fin"
                  valeur={modalites.periodeFin}
                  onChange={(v) => setModalites({ ...modalites, periodeFin: v })}
                />
                <label className="devis-form-field">
                  <span>Lieu</span>
                  <input
                    type="text"
                    value={modalites.modalitesLieu}
                    onChange={(e) => setModalites({ ...modalites, modalitesLieu: e.target.value })}
                  />
                </label>
              </div>

              <div className="devis-form-row">
                <label className="devis-form-field">
                  <span>Format souhaité</span>
                  <SelectRecherche
                    value={modalites.modalitesFormatSouhaite}
                    onChange={(e) => setModalites({ ...modalites, modalitesFormatSouhaite: e.target.value })}
                  >
                    <option value="">—</option>
                    {[...FORMATS, { value: 'mixte', label: 'Mixte (INTRA / INTER)' }].map((f) => (
                      <option key={f.value} value={f.value}>
                        {f.label}
                      </option>
                    ))}
                  </SelectRecherche>
                </label>
                <label className="devis-form-field" style={{ flex: 2 }}>
                  <span>Contraintes</span>
                  <input
                    type="text"
                    value={modalites.modalitesContraintes}
                    onChange={(e) => setModalites({ ...modalites, modalitesContraintes: e.target.value })}
                  />
                </label>
              </div>

              <div className="devis-form-section-title">Textes du devis</div>
              <label className="devis-form-field">
                <span>Résumé de l’échange (affiché en tête du PDF)</span>
                <textarea
                  rows={3}
                  value={remises.resumeEchange}
                  onChange={(e) => setRemises({ ...remises, resumeEchange: e.target.value })}
                />
              </label>

              <div className="devis-form-section-title">Remises et offre exceptionnelle</div>
              <label className="devis-form-field">
                <span>Conditions des remises (note sous le tableau)</span>
                <textarea
                  rows={2}
                  value={remises.conditionRemise}
                  onChange={(e) => setRemises({ ...remises, conditionRemise: e.target.value })}
                />
              </label>
              <div className="devis-form-row">
                <label className="devis-form-field" style={{ flex: 2 }}>
                  <span>Offre exceptionnelle (libellé, sous les remises)</span>
                  <input
                    type="text"
                    placeholder="Offre de bienvenue, 2e journée offerte…"
                    value={remises.offreLibelle}
                    onChange={(e) => setRemises({ ...remises, offreLibelle: e.target.value })}
                  />
                </label>
                <label className="devis-form-field">
                  <span>Montant de l&apos;offre (€ HT)</span>
                  <input
                    type="number"
                    step="0.01"
                    min="0"
                    placeholder="0"
                    value={remises.offreHt}
                    onChange={(e) => setRemises({ ...remises, offreHt: e.target.value })}
                  />
                </label>
              </div>

              <div className="devis-form-section-title">Formations</div>
              {lignes.map((l, i) => (
                <div key={i} className={`devis-ligne-edit${l.estOption ? ' devis-ligne-option' : ''}`}>
                  <div className="devis-ligne-edit-top">
                    <span className="devis-ligne-numero" aria-label={`Ligne ${i + 1}`}>
                      {i + 1}
                    </span>
                    {l.estOption && (
                      <span className="devis-option-badge" title="Proposée en plus de la ligne de base">
                        Option
                      </span>
                    )}
                    <SelectRecherche
                      className="devis-ligne-edit-libelle"
                      value={l.formationId}
                      onChange={(e) => majLigne(i, 'formationId', e.target.value)}
                      required
                    >
                      <option value="">— Choisir une formation —</option>
                      {formations.map((f) => (
                        <option key={f.id} value={f.id}>
                          {f.code} — {f.intitule}
                        </option>
                      ))}
                    </SelectRecherche>
                  </div>

                  <div className="devis-form-row">
                    <label className="devis-form-field">
                      <span>Format</span>
                      <SelectRecherche
                        value={l.format}
                        onChange={(e) => majLigne(i, 'format', e.target.value)}
                      >
                        {FORMATS.map((f) => (
                          <option key={f.value} value={f.value}>
                            {f.label}
                          </option>
                        ))}
                      </SelectRecherche>
                    </label>
                    <SelectSessionCalendrier
                      sessions={sessionsCalendrier}
                      formationId={l.formationId}
                      format={l.format}
                      value={l.calendrierId}
                      onChange={(e) => majLigne(i, 'calendrierId', e.target.value)}
                    />
                    <label className="devis-form-field">
                      <span>Durée (j)</span>
                      <input
                        type="number"
                        step="0.5"
                        min="0.5"
                        value={l.dureeJours}
                        onChange={(e) => majLigne(i, 'dureeJours', e.target.value)}
                        required
                      />
                    </label>
                    <label className="devis-form-field">
                      <span>Participants</span>
                      <input
                        type="number"
                        min="1"
                        value={l.nbParticipants}
                        onChange={(e) => majLigne(i, 'nbParticipants', e.target.value)}
                        required
                      />
                    </label>
                    <label className="devis-form-field">
                      <span>P.U. HT (€)</span>
                      <input
                        type="number"
                        step="0.01"
                        min="0"
                        placeholder="selon la grille"
                        value={l.prixUnitaireHt}
                        onChange={(e) => majLigne(i, 'prixUnitaireHt', e.target.value)}
                        title="Vide : tarif de la grille. Un prix saisi ne vaut que pour ce devis."
                      />
                    </label>
                    <label className="devis-form-field">
                      <span>Remise (%)</span>
                      <input
                        type="number"
                        step="0.5"
                        min="0"
                        max="100"
                        value={l.tauxRemise}
                        onChange={(e) => majLigne(i, 'tauxRemise', e.target.value)}
                      />
                    </label>
                    <label className="devis-form-field">
                      <span>Remise par journée (€)</span>
                      <input
                        type="number"
                        step="0.01"
                        min="0"
                        placeholder="0"
                        value={l.remiseJourneeHt}
                        onChange={(e) => majLigne(i, 'remiseJourneeHt', e.target.value)}
                        title="Montant retiré à chaque journée facturée (par participant quand la ligne est facturée par participant)."
                      />
                    </label>
                  </div>

                  <div className="devis-form-row">
                    <label className="devis-form-field">
                      <span>
                        {l.estOption ? 'Libellé de l’option affiché sur le devis' : 'Libellé affiché sur le devis'}
                      </span>
                      <input
                        type="text"
                        placeholder="Intitulé de la formation"
                        value={l.libelle}
                        onChange={(e) => majLigne(i, 'libelle', e.target.value)}
                      />
                    </label>
                    <label className="devis-form-field">
                      <span>Libellé du groupe (facultatif)</span>
                      <input
                        type="text"
                        placeholder="SSCT Groupe 1"
                        value={l.groupeLibelle}
                        onChange={(e) => majLigne(i, 'groupeLibelle', e.target.value)}
                      />
                    </label>
                    {(Number(l.tauxRemise) > 0 || Number(l.remiseJourneeHt) > 0) && (
                      <label className="devis-form-field">
                        <span>Motif de la remise</span>
                        <input
                          type="text"
                          placeholder="Remise commerciale"
                          value={l.motifRemise}
                          onChange={(e) => majLigne(i, 'motifRemise', e.target.value)}
                        />
                      </label>
                    )}
                    {l.estOption && (
                      <label className="devis-form-field devis-option-retenue">
                        <span>Option retenue</span>
                        <input
                          type="checkbox"
                          checked={Boolean(l.optionRetenue)}
                          onChange={(e) => majLigne(i, 'optionRetenue', e.target.checked)}
                        />
                        <small>
                          {l.optionRetenue
                            ? 'Incluse au total du devis.'
                            : 'Affichée « en option » : hors total tant qu’elle n’est pas retenue.'}
                        </small>
                      </label>
                    )}
                  </div>

                  {/* La corbeille seule à droite, sous les champs : loin du
                      choix de la formation, comme dans la modification du devis. */}
                  <div className="devis-ligne-edit-pied devis-ligne-edit-pied-droite">
                    {!l.estOption && (
                      <button
                        type="button"
                        className="devis-btn devis-btn-sm"
                        onClick={() => ajouterOption(i)}
                        disabled={!l.formationId}
                        title="Proposer une autre formule de cette formation, en option"
                      >
                        + Ajouter une option
                      </button>
                    )}
                    <button
                      type="button"
                      className="devis-icone-btn devis-icone-btn-danger"
                      onClick={() => retirerLigne(i)}
                      disabled={!l.estOption && nbLignesDeBase <= 1}
                      aria-label="Retirer cette ligne"
                      title={
                        !l.estOption && nbLignesDeBase <= 1
                          ? 'Un devis doit garder au moins une formation hors option'
                          : 'Retirer cette ligne'
                      }
                    >
                      <IconeCorbeille />
                    </button>
                  </div>
                </div>
              ))}

              <button type="button" className="devis-btn devis-btn-sm" onClick={ajouterLigne}>
                + Ajouter une formation
              </button>

              <div className="devis-apercu-total">
                Estimation : <strong>{formatMontant(apercu)}</strong> HT
                <span className="devis-apercu-note">
                  Au tarif catalogue ; montant définitif à l’enregistrement.
                </span>
              </div>

              <div className="devis-externe">
                <label className="devis-form-field">
                  <span>Devis établi hors de l’application (facultatif)</span>
                  <input
                    type="file"
                    accept="application/pdf,.pdf"
                    onChange={(e) => setFichierExterne(e.target.files?.[0] || null)}
                    disabled={saving}
                  />
                </label>
                <p className="devis-apercu-note">
                  Joignez le PDF : le devis sera marqué <strong>externe</strong> et ce fichier fera foi — il ne sera
                  jamais régénéré. Les lignes ci-dessus restent la référence des montants et du contrat.
                </p>
              </div>

              <BarreProgression valeur={progressionEnregistrement} libelle="Création du devis" estimation />
              <div className="devis-modal-actions">
                <button type="button" className="devis-btn" onClick={onClose} disabled={saving}>
                  Annuler
                </button>
                <button
                  type="submit"
                  className="devis-btn devis-btn-primary"
                  disabled={saving || !valide}
                >
                  {saving ? 'Création' : 'Créer le devis'}
                </button>
              </div>
            </form>
          )}
        </div>
      </div>
    </div>
  );
}

export default DevisManuelPanel;
